import * as React from "react";
import Stack from "@mui/material/Stack";
import { QRCodeCanvas } from "qrcode.react";
import Typography from "@mui/material/Typography";
import { TextField } from "@mui/material";
import Button from "@mui/material/Button";
import { useUserSession } from "hooks/useUserSession";
import { useMutation } from "react-query";
import { Auth, CognitoUser } from "@aws-amplify/auth";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { useTranslation } from "react-i18next";

interface Props {
  user: CognitoUser;
  totpSecret: string;
  onSuccess: () => void;
  onClose: () => void;
}

const SetupTotp: React.FC<Props> = ({ user, totpSecret, onSuccess, onClose }) => {
  const { t } = useTranslation();
  const { username } = useUserSession();
  const [authCode, setAuthCode] = React.useState<string>();

  const {
    mutate: verifyTotpToken,
    isLoading: isVerifyTotpTokenLoading,
    error: verifyTotpTokenError
  } = useMutation({
    mutationFn: async ({ user, code }: { user: any; code: string }) => {
      await Auth.verifyTotpToken(user, code);
      await Auth.setPreferredMFA(user, "TOTP");
    },
    onSuccess
  });

  const submitAuthCode = React.useCallback(() => {
    if (!authCode) {
      return;
    }
    window.console.log("Calling submitAuthCode");
    verifyTotpToken({ user, code: authCode });
  }, [authCode, user, verifyTotpToken]);

  return (
    <Stack spacing={2}>
      <Typography fontWeight="bold">{t("mfa.setup.title")}</Typography>
      <Stack>
        <Typography>{t("mfa.setup.step1Title")}</Typography>
        <Typography variant="caption">{t("mfa.setup.step1Description")}</Typography>
      </Stack>
      <Stack>
        <Typography>{t("mfa.setup.step2Title")}</Typography>
        <Typography variant="caption">{t("mfa.setup.step2Description")}</Typography>
      </Stack>
      <Stack alignItems="center">
        <QRCodeCanvas value={`otpauth://totp/AWSCognito:${username}?secret=${totpSecret}&issuer=LLS`} />
      </Stack>
      <Stack>
        <Typography>{t("mfa.setup.step3Title")}</Typography>
        <Typography variant="caption">{t("mfa.setup.step3Description")}</Typography>
      </Stack>
      <Stack alignItems="center" spacing={2}>
        <TextField
          placeholder={t("mfa.setup.enterCodePlaceholder")}
          variant="outlined"
          value={authCode}
          disabled={isVerifyTotpTokenLoading}
          onChange={e => setAuthCode(e.target.value)}
        />
        {verifyTotpTokenError && <Typography color="error.main">{t("mfa.setup.invalidCode")}</Typography>}
        <Stack direction="row" spacing={1} alignItems="center">
          <LoadingButton isLoading={isVerifyTotpTokenLoading} variant="contained" onClick={submitAuthCode}>
            {t("mfa.setup.submit")}
          </LoadingButton>
          <Button onClick={onClose}>{t("mfa.setup.cancel")}</Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export { SetupTotp };
